/**
 * UserStatusContext - Trial & Paywall State
 *
 * Fetches and caches the user's trial/paywall status from the backend.
 * Provides hooks for checking paywall state and triggering the paywall modal.
 *
 * Separate from AuthContext for single-responsibility: auth handles sessions,
 * this context handles business rules (trial, paywall, plan).
 *
 * Consumed by:
 * - ProtectedRoute (to show locked screen)
 * - PaywallModal (to display paywall)
 * - apiClient interceptor (to react to PAYWALL_REQUIRED responses)
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { apiGet } from '@/services/apiClient';
import { setOnPaywallRequired } from '@/services/apiClient';

// ============================================================================
// TYPES
// ============================================================================

export interface PlanLimits {
  carousel_limit: number;
  export_limit: number;
  generation_limit: number;
}

export interface PlanUsage {
  carousels: number;
  generations: number;
  exports: number;
}

export interface UserStatus {
  instagramHandle: string | null;
  instagramLocked: boolean;
  trialUsed: boolean;
  paywallActive: boolean;
  plan: 'free' | 'pro' | 'enterprise';
  // New fields from subscription system
  planName?: string;
  limits?: PlanLimits;
  usage?: PlanUsage;
  dailyCarouselUsage?: number;
  canCreateCarousel?: boolean;
  canGenerate?: boolean;
  canExport?: boolean;
  // Trial V2
  isTrialing?: boolean;
  // Onboarding
  onboardingCompleted?: boolean;
  /** Senha temporária gerada pelo suporte: troca obrigatória antes de qualquer outra tela. */
  mustChangePassword?: boolean;
}

interface UserStatusContextType {
  status: UserStatus | null;
  loading: boolean;
  isPaywalled: boolean;
  showPaywall: boolean;
  setShowPaywall: (show: boolean) => void;
  refreshStatus: () => Promise<void>;
  /**
   * Troca obrigatória de senha pendente. /api/user/status é a fonte
   * autoritativa; sem status (ainda carregando ou erro), vale a flag do
   * AuthContext (signin/refresh ou 403 must_change_password).
   */
  passwordChangePending: boolean;
}

// ============================================================================
// CONTEXT
// ============================================================================

const UserStatusContext = createContext<UserStatusContextType | undefined>(undefined);

// ============================================================================
// PROVIDER
// ============================================================================

export const UserStatusProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, mustChangePassword: authMustChangePassword } = useAuth();
  const [status, setStatus] = useState<UserStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [showPaywall, setShowPaywall] = useState(false);
  const initialFetchDone = useRef(false);

  const fetchStatus = useCallback(async () => {
    if (!user) {
      setStatus(null);
      setLoading(false);
      initialFetchDone.current = false;
      return;
    }

    // Only show global loading on initial fetch (no status yet).
    // Subsequent refreshes update silently to avoid unmounting
    // protected routes via ProtectedRoute's loading guard.
    if (!initialFetchDone.current) {
      setLoading(true);
    }
    try {
      const data = await apiGet<UserStatus>('/api/user/status');
      setStatus(data);
    } catch {
      // Fallback: assume not paywalled on error (don't block the user)
      setStatus(null);
    } finally {
      initialFetchDone.current = true;
      setLoading(false);
    }
  }, [user]);

  // Fetch status when user authenticates
  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Register paywall interceptor on the API client.
  // Only triggers on PAYWALL_REQUIRED (403) from guarded routes
  // (e.g. trying to create a second carousel).
  // IMPORTANT: Never show the paywall modal in the editor — the user must be
  // able to freely edit, generate images, and export their trial carousel.
  // Paywall enforcement happens when returning to Dashboard or creating a 2nd carousel.
  useEffect(() => {
    setOnPaywallRequired(() => {
      if (window.location.pathname.startsWith('/editor/')) {
        return;
      }
      setShowPaywall(true);
    });
    return () => setOnPaywallRequired(null);
  }, []);

  // A API respondeu 403 must_change_password (ex.: admin gerou senha temporária
  // com o usuário logado): rebusca o status para ele virar a fonte da verdade.
  useEffect(() => {
    if (authMustChangePassword && status && !status.mustChangePassword) {
      fetchStatus();
    }
  }, [authMustChangePassword]); // eslint-disable-line react-hooks/exhaustive-deps

  const passwordChangePending = status ? status.mustChangePassword === true : authMustChangePassword;

  // A user who has never used their trial is NEVER paywalled,
  // even if the subscription record is missing (defensive).
  const freshTrial = status?.trialUsed === false && !status?.paywallActive;

  // A user is paywalled if canCreateCarousel is explicitly false (new system).
  // Falls back to legacy check only when new system is unavailable.
  // Exception: fresh trial users are always exempt.
  const isPaywalled = !freshTrial && (
    status?.canCreateCarousel !== undefined
      ? status.canCreateCarousel === false
      : !!((status?.paywallActive || status?.trialUsed) && status?.plan === 'free')
  );

  return (
    <UserStatusContext.Provider value={{
      status,
      loading,
      isPaywalled,
      showPaywall,
      setShowPaywall,
      refreshStatus: fetchStatus,
      passwordChangePending,
    }}>
      {children}
    </UserStatusContext.Provider>
  );
};

// ============================================================================
// HOOK
// ============================================================================

export const useUserStatus = (): UserStatusContextType => {
  const context = useContext(UserStatusContext);
  if (context === undefined) {
    throw new Error('useUserStatus must be used within a UserStatusProvider');
  }
  return context;
};
